import get from "lodash/get";
import React, {
  ComponentProps,
  ReactElement,
  useCallback,
  useEffect,
  useRef,
  useState,
} from "react";

import { FetchController } from "./FetchController";

type ObjId = string | number;

type FetchControllerProps = ComponentProps<typeof FetchController>;

interface SimpleFetchControllerProps<TObject = unknown>
  extends Omit<
    FetchControllerProps,
    "fetchObject" | "object" | "objectLoaded"
  > {
  children?: ReactElement;
  errorChild?: ReactElement;
  fetchObject: (objId: ObjId) => Promise<unknown>;
}

function SimpleFetchController<TObject = unknown>({
  children,
  errorChild,
  fetchObject,
  ...restProps
}: SimpleFetchControllerProps<TObject>) {
  const isMountedRef = useRef(false);

  const [hasError, setHasError] = useState(false);
  const [object, setObject] = useState<TObject | null>(null);
  const [objectLoaded, setObjectLoaded] = useState(false);

  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  const fetchWrapper = useCallback(
    (objId: ObjId) => {
      return fetchObject(objId)
        .then((resp) => {
          if (!isMountedRef.current) return;

          setHasError(false);
          setObject(get(resp, "data.data") as TObject);
          setObjectLoaded(true);
        })
        .catch(() => {
          if (!isMountedRef.current) return;

          setHasError(true);
          setObject(null);
          setObjectLoaded(false);
        });
    },
    [fetchObject],
  );

  const beginFetch = useCallback(
    async (objId: ObjId) => {
      if (isMountedRef.current) {
        setHasError(false);
        setObject(null);
        setObjectLoaded(false);
      }

      return fetchWrapper(objId);
    },
    [fetchWrapper],
  );

  if (hasError) {
    return errorChild ?? <div>Error fetching object.</div>;
  }

  return (
    <FetchController
      {...restProps}
      fetchObject={beginFetch}
      object={object}
      objectLoaded={objectLoaded}
    >
      {children}
    </FetchController>
  );
}

export { SimpleFetchController };
export default SimpleFetchController;
